import { useState, useRef } from 'react'
import { useDocumentTitle } from '../hooks/useDocumentTitle'
import { Link } from 'react-router-dom'
import { Plus, FileCheck, Bookmark, Trash2, Check, Pencil, ChevronDown } from 'lucide-react'
import { useApi } from '../hooks/useApi'
import { useData } from '../hooks/useData'
import { getTierColor, formatDate, calculateAverageSatisfaction } from '../lib/utils'
import { TIERS, STATUSES } from '../lib/constants'
import MultiSelect from '../components/MultiSelect'
import Popover from '../components/Popover'
import LoadingSpinner from '../components/LoadingSpinner'
import type { Project, SurveyResponse, Settings, SavedView } from '../types'

const EMPTY_FILTERS = { portfolio: [], tier: [], status: [], source: [], team: [], owner: [] }

export default function Projects() {
  useDocumentTitle('Projects')
  const api = useApi()
  const { data, loading } = useData(() =>
    Promise.all([api.getProjects(), api.getSurveyResponses(), api.getSettings(), api.getViews('projects')])
      .then(([projects, responses, settings, views]) => ({ projects, responses, settings, views }))
  )
  const [search, setSearch] = useState('')
  const [filters, setFilters] = useState<Record<string, string[]>>(EMPTY_FILTERS)
  // Delta-based view state: avoids calling setState during render
  const [addedViews, setAddedViews]       = useState<SavedView[]>([])
  const [deletedIds, setDeletedIds]       = useState<Set<string>>(new Set())
  const [activeViewId, setActiveViewId]   = useState<string | null>(null)
  const [saveInput, setSaveInput]         = useState('')
  const [showSaveInput, setShowSaveInput] = useState(false)
  const [saveError, setSaveError]         = useState('')
  const [saving, setSaving]               = useState(false)
  const [renamingId, setRenamingId]       = useState<string | null>(null)
  const [renameInput, setRenameInput]     = useState('')
  const [renamedNames, setRenamedNames]   = useState<Record<string, string>>({})
  const [viewsOpen, setViewsOpen]         = useState(false)
  const viewsRef = useRef<HTMLDivElement>(null)

  if (loading || !data) return <LoadingSpinner />

  const { projects, responses, settings, views: rawViews } = data as {
    projects: Project[]; responses: SurveyResponse[]; settings: Settings; views: SavedView[]
  }

  // Merge server list with local mutations - no setState in render body
  const savedViews: SavedView[] = [
    ...(rawViews ?? []).filter(v => !deletedIds.has(v.id)),
    ...addedViews,
  ]

  const applyView = (v: SavedView) => {
    setSearch(v.filters.search ?? '')
    setFilters({
      portfolio: v.filters.portfolio ?? [],
      tier:      v.filters.tier      ?? [],
      status:    v.filters.status    ?? [],
      source:    v.filters.source    ?? [],
      team:      v.filters.team      ?? [],
      owner:     v.filters.owner     ?? [],
    })
    setActiveViewId(v.id)
  }

  const handleSaveView = async () => {
    if (!saveInput.trim()) return
    setSaving(true); setSaveError('')
    try {
      const created = await api.createView({ name: saveInput.trim(), page: 'projects', filters: { search, ...filters } }) as SavedView
      setAddedViews(vs => [...vs, created])
      setActiveViewId(created.id)
      setSaveInput(''); setShowSaveInput(false)
    } catch {
      setSaveError('Failed to save, try again')
    } finally {
      setSaving(false)
    }
  }

  const handleStartRename = (v: SavedView) => {
    setRenamingId(v.id)
    setRenameInput(renamedNames[v.id] ?? v.name)
  }

  const handleRenameSubmit = async (id: string) => {
    const newName = renameInput.trim()
    setRenamingId(null)
    if (!newName) return
    const previous = renamedNames[id]
    setRenamedNames(m => ({ ...m, [id]: newName }))
    await api.updateView(id, { name: newName }).catch(() => {
      setRenamedNames(m => {
        const n = { ...m }
        if (previous !== undefined) { n[id] = previous } else { delete n[id] }
        return n
      })
    })
  }

  const handleDeleteView = async (id: string) => {
    setDeletedIds(ids => new Set([...ids, id]))
    if (activeViewId === id) setActiveViewId(null)
    await api.deleteView(id).catch(() => {
      // Revert optimistic delete on failure
      setDeletedIds(ids => { const s = new Set(ids); s.delete(id); return s })
    })
  }

  const clearFilters = () => {
    setFilters(EMPTY_FILTERS); setSearch(''); setActiveViewId(null)
  }

  // Project "owner" = createdBy (the same identity the reminder emails group by).
  // Options list only people who currently own at least one project.
  const ownerLabel = (p: Project) => p.createdBy?.name || p.createdBy?.email || ''
  const ownerOptions = Array.from(new Set(projects.map(ownerLabel).filter(Boolean))).sort((a, b) => a.localeCompare(b))

  const filterConfig = [
    { key: 'portfolio', label: 'Sub-Portfolio', options: settings.subPortfolios, searchable: true },
    { key: 'tier', label: 'Tier', options: TIERS },
    { key: 'status', label: 'Status', options: STATUSES },
    { key: 'source', label: 'Source', options: ['Asana', 'Manual'] },
    { key: 'team', label: 'Team', options: settings.teams },
    { key: 'owner', label: 'Owner', options: ownerOptions, searchable: true },
  ]

  // Index responses by projectId for O(1) lookup
  const responsesByProject = new Map<string, number>()
  responses.forEach(r => {
    responsesByProject.set(r.projectId, (responsesByProject.get(r.projectId) ?? 0) + 1)
  })

  const hasFilters = Object.values(filters).some(v => v.length > 0)

  const filtered = projects.filter(p => {
    if (search && !p.name.toLowerCase().includes(search.toLowerCase())) return false
    if (filters.portfolio.length && !filters.portfolio.includes(p.subPortfolio)) return false
    if (filters.tier.length && !filters.tier.includes(p.tier)) return false
    if (filters.status.length && !filters.status.includes(p.status)) return false
    if (filters.source.length) {
      const source = p.asanaProjectId ? 'Asana' : 'Manual'
      if (!filters.source.includes(source)) return false
    }
    if (filters.team.length && !filters.team.includes(p.team || '')) return false
    if (filters.owner.length && !filters.owner.includes(ownerLabel(p))) return false
    return true
  })

  const getPipeline = (p: Project) => {
    const responseCount = responsesByProject.get(p.id) ?? 0
    // Expected users only required for 'users' adoption mode.
    const adoptionOk = (p.adoptionMode || 'users') !== 'users' || !!p.expectedUsers
    const skip = !!p.skipStakeholders
    const stakeholdersOk = skip || p.stakeholders.length > 0
    const configured = !!(p.dueDate && p.actualGoLive && p.deliveredInFull && adoptionOk && stakeholdersOk)
    const surveySent = configured && (skip || !!p.surveySentAt)
    const complete = surveySent && (skip || responseCount >= p.stakeholders.length)
    return { configured, surveySent, complete }
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Projects</h1>
          <p className="text-slate-500 text-sm">Manage and track all projects</p>
        </div>
        <Link to="/projects/new" className="btn-primary text-white px-4 py-2 rounded-lg text-sm font-semibold flex items-center gap-2">
          <Plus className="w-4 h-4" /> Add Project
        </Link>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 p-4 mb-3 flex items-center gap-3 flex-wrap">
        <span className="text-xs font-semibold text-slate-500 uppercase">Filters:</span>
        {filterConfig.map(f => (
          <MultiSelect key={f.key} values={filters[f.key]} onChange={v => setFilters({ ...filters, [f.key]: v })} options={f.options} placeholder={f.label} searchable={f.searchable} />
        ))}
        {hasFilters && (
          <button onClick={clearFilters} className="text-xs text-danone-cyan font-semibold hover:text-danone-blue">Clear</button>
        )}
        <input type="text" placeholder="Search projects..." value={search} onChange={e => setSearch(e.target.value)}
          className="ml-auto bg-slate-50 border border-slate-200 rounded-lg px-4 py-2 text-sm w-64" />
      </div>

      {/* Saved views dropdown */}
      <div ref={viewsRef} className="relative mb-5 inline-block">
        <button
          onClick={() => setViewsOpen(o => !o)}
          className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-sm font-medium transition-colors ${
            activeViewId
              ? 'border-[#005EB8] bg-[#005EB8]/5 text-[#005EB8]'
              : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300'
          }`}
        >
          <Bookmark className="w-3.5 h-3.5" />
          {activeViewId ? (renamedNames[activeViewId] ?? savedViews.find(v => v.id === activeViewId)?.name ?? 'View') : 'Views'}
          {savedViews.length > 0 && !activeViewId && (
            <span className="text-xs text-slate-400 font-normal">{savedViews.length}</span>
          )}
          <ChevronDown className={`w-3.5 h-3.5 text-slate-400 transition-transform ${viewsOpen ? 'rotate-180' : ''}`} />
        </button>

        <Popover open={viewsOpen} anchorRef={viewsRef} onClose={() => setViewsOpen(false)} width="content" className="w-64" label="Saved views">
          <div className="py-1 min-h-0 overflow-y-auto">
            {savedViews.length > 0 && (
              <>
                {savedViews.map(v => {
                  const isActive = v.id === activeViewId
                  const displayName = renamedNames[v.id] ?? v.name
                  return (
                    <div key={v.id} className={`flex items-center gap-2 px-3 py-2 hover:bg-slate-50 group ${isActive ? 'bg-[#005EB8]/5' : ''}`}>
                      {renamingId === v.id ? (
                        <input
                          autoFocus
                          data-escape-local
                          aria-label={`Rename ${displayName}`}
                          value={renameInput}
                          onChange={e => setRenameInput(e.target.value)}
                          onKeyDown={e => {
                            if (e.key === 'Enter') handleRenameSubmit(v.id)
                            if (e.key === 'Escape') setRenamingId(null)
                          }}
                          onBlur={() => handleRenameSubmit(v.id)}
                          className="flex-1 text-sm outline-none border-b border-[#005EB8] bg-transparent"
                        />
                      ) : (
                        <button
                          data-option
                          aria-selected={isActive}
                          onClick={() => { applyView(v); setViewsOpen(false) }}
                          className={`flex-1 text-left text-sm truncate rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-danone-cyan ${isActive ? 'font-semibold text-[#005EB8]' : 'text-slate-700'}`}
                        >
                          {displayName}
                        </button>
                      )}
                      <button onClick={() => handleStartRename(v)} aria-label={`Rename ${displayName}`}
                        className="p-1 text-slate-300 hover:text-[#005EB8] opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 focus:opacity-100 transition-opacity flex-shrink-0">
                        <Pencil className="w-3.5 h-3.5" />
                      </button>
                      <button onClick={() => handleDeleteView(v.id)} aria-label={`Delete ${displayName}`}
                        className="p-1 text-slate-300 hover:text-red-400 opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 focus:opacity-100 transition-opacity flex-shrink-0">
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  )
                })}
                <div className="border-t border-slate-100 mt-1 pt-1" />
              </>
            )}

            {showSaveInput ? (
              <div className="px-3 py-2 space-y-2">
                <input
                  autoFocus
                  data-escape-local
                  aria-label="View name"
                  value={saveInput}
                  onChange={e => { setSaveInput(e.target.value); setSaveError('') }}
                  onKeyDown={e => { if (e.key === 'Enter') handleSaveView(); if (e.key === 'Escape') { setShowSaveInput(false); setSaveInput('') } }}
                  placeholder="View name…"
                  className="w-full border border-slate-200 rounded-lg px-2.5 py-1.5 text-sm outline-none focus:border-[#005EB8]"
                />
                <div className="flex items-center gap-2">
                  <button onClick={handleSaveView} disabled={saving || !saveInput.trim()}
                    className="flex items-center gap-1 px-3 py-1 bg-[#005EB8] text-white rounded-lg text-xs font-semibold disabled:opacity-40">
                    <Check className="w-3 h-3" /> Save
                  </button>
                  <button onClick={() => { setShowSaveInput(false); setSaveInput(''); setSaveError('') }}
                    className="text-xs text-slate-400 hover:text-slate-600">Cancel</button>
                  {saveError && <span className="text-xs text-red-500">{saveError}</span>}
                </div>
              </div>
            ) : (
              <button
                data-option
                onClick={() => setShowSaveInput(true)}
                className="w-full text-left px-3 py-2 text-sm text-slate-500 hover:bg-slate-50 hover:text-[#005EB8] focus-visible:bg-slate-100 focus:outline-none transition-colors"
              >
                + Save current filters as view
              </button>
            )}
          </div>
        </Popover>
      </div>

      <div className="mb-4 flex items-center gap-6 text-xs text-slate-500">
        <span className="font-semibold">Pipeline stages:</span>
        {[
          ['✓', 'bg-emerald-100 text-[#00843D]', 'Synced'],
          ['!', 'bg-amber-100 text-[#F88806]', 'Needs Config'],
          ['◉', 'bg-blue-100 text-blue-600', 'Awaiting Responses'],
          ['○', 'bg-slate-100 text-slate-400', 'Pending'],
        ].map(([icon, cls, label]) => (
          <span key={label as string} className="flex items-center gap-1.5">
            <span className={`w-4 h-4 ${cls} rounded-full flex items-center justify-center text-[10px] font-bold`}>{icon}</span> {label}
          </span>
        ))}
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="table-header border-b border-slate-200">
              {['Project', 'Source', 'Tier', 'Pipeline', 'Go-Live', 'Satisfaction'].map(h => (
                <th key={h} className="px-5 py-4 text-left text-xs font-bold text-slate-600 uppercase tracking-wide">{h}</th>
              ))}
              <th className="px-5 py-4 text-center text-xs font-bold text-slate-600 uppercase tracking-wide">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map(project => {
              const pipeline = getPipeline(project)
              const projectResponses = responses.filter(r => r.projectId === project.id)
              const avgSat = calculateAverageSatisfaction(projectResponses)
              const stages = [
                { done: true },
                { done: pipeline.configured, warn: !pipeline.configured },
                { done: pipeline.surveySent },
                { done: pipeline.complete, active: pipeline.surveySent && !pipeline.complete },
              ]

              return (
                <tr key={project.id} className="border-b border-slate-100 hover:bg-slate-50/50">
                  <td className="px-5 py-4">
                    <Link to={`/projects/${project.id}`} className="font-semibold text-slate-800 hover:text-[#005EB8]">{project.name}</Link>
                    <span className="inline-block mt-1 px-2 py-0.5 bg-slate-100 text-slate-600 text-[10px] font-semibold rounded-full">{project.subPortfolio}</span>
                  </td>
                  <td className="px-5 py-4">
                    <span className="flex items-center gap-1.5 text-xs font-medium text-slate-600">
                      <span className={`w-2 h-2 rounded-full ${project.asanaProjectId ? 'bg-[#EF3340]' : 'bg-slate-400'}`} />
                      {project.asanaProjectId ? 'Asana' : 'Manual'}
                      {project.templateMatch && <span title="PD Template"><FileCheck className="w-3 h-3 text-[#00843D]" /></span>}
                    </span>
                  </td>
                  <td className="px-5 py-4">
                    <span className={`px-2.5 py-1 text-xs font-bold rounded-full ${getTierColor(project.tier)}`}>{project.tier}</span>
                  </td>
                  <td className="px-5 py-4">
                    <div className="flex items-center gap-1">
                      {stages.map((s, i) => (
                        <span key={i} className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                          s.done ? 'bg-emerald-100 text-[#00843D]' :
                          s.warn ? 'bg-amber-100 text-[#F88806]' :
                          s.active ? 'bg-blue-100 text-blue-600' : 'bg-slate-100 text-slate-400'
                        }`}>
                          {s.done ? '✓' : s.warn ? '!' : s.active ? '◉' : '○'}
                        </span>
                      ))}
                    </div>
                  </td>
                  <td className="px-5 py-4 text-sm text-slate-600">
                    {(project.actualGoLive || project.dueDate)
                      ? formatDate(project.actualGoLive || project.dueDate)
                      : '-'}
                  </td>
                  <td className="px-5 py-4">
                    {avgSat ? <span className="font-bold text-[#00ACED]">{avgSat}/5</span> : <span className="text-slate-400">-</span>}
                  </td>
                  <td className="px-5 py-4 text-center">
                    <Link to={`/projects/${project.id}`} className="text-[#005EB8] hover:text-[#00AEEF] text-sm font-semibold">View</Link>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
        {filtered.length === 0 && <div className="text-center py-12 text-slate-500">No projects found</div>}
      </div>
    </div>
  )
}
